import type { ArAgingResponse } from '@hesabat/contracts';
import type { CsvCell } from './csv';
import { formatMoney, isZero } from './money';

export const AGING_COLUMNS = [
  ['current', 'غير مستحق'],
  ['days1to30', '1–30 يومًا'],
  ['days31to60', '31–60'],
  ['days61to90', '61–90'],
  ['over90', 'أكثر من 90'],
  ['unappliedCredit', 'تسوية غير مخصصة'],
  ['total', 'الصافى'],
] as const;

type Amounts = ArAgingResponse['totals'];

/** أعمار عملاء أو موردين بشكل واحد — الطرف فى `partyId`. */
export interface AgingView {
  asOf: string;
  rows: (Amounts & { partyId: string; code: string; nameAr: string })[];
  totals: Amounts;
  controlAccountCode: string;
  controlBalance: string;
  unreconciled: string;
}

export function agingHeader(partyLabel: string): string[] {
  return ['الكود', partyLabel, ...AGING_COLUMNS.map(([, label]) => label)];
}

export function agingRows(aging: AgingView): CsvCell[][] {
  return [
    ...aging.rows.map((row) => [row.code, row.nameAr, ...AGING_COLUMNS.map(([key]) => row[key])]),
    ['', 'الإجمالى', ...AGING_COLUMNS.map(([key]) => aging.totals[key])],
    ['', 'رصيد الحساب الرقابى ' + aging.controlAccountCode + ' فى الدفتر', aging.controlBalance],
    ['', 'غير مطابق (قيود لا تخص مستندًا)', aging.unreconciled],
  ];
}

/**
 * أعمار الأرصدة المفتوحة مع مطابقتها بالحساب الرقابى. الفرق يُعرض صريحًا: قيود على
 * الحساب الرقابى لا تخص فاتورة أو تسوية (يدوية أو افتتاحية) — تستوجب المراجعة لا الإخفاء.
 */
export default function AgingTable({
  aging,
  partyLabel,
  onOpenParty,
}: {
  aging: AgingView;
  partyLabel: string;
  onOpenParty: (partyId: string) => void;
}) {
  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>الكود</th>
              <th>{partyLabel}</th>
              {AGING_COLUMNS.map(([key, label]) => (
                <th key={key}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aging.rows.map((row) => (
              <tr
                key={row.partyId}
                className="clickable"
                tabIndex={0}
                onClick={() => onOpenParty(row.partyId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onOpenParty(row.partyId);
                }}
              >
                <td className="mono">{row.code}</td>
                <td>{row.nameAr}</td>
                {AGING_COLUMNS.map(([key]) => (
                  <td key={key} className="num-cell">
                    {isZero(row[key]) ? '' : formatMoney(row[key])}
                  </td>
                ))}
              </tr>
            ))}
            {aging.rows.length === 0 && (
              <tr>
                <td colSpan={AGING_COLUMNS.length + 2} className="sub">
                  لا أرصدة مفتوحة فى هذا التاريخ.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="closing">
              <td colSpan={2}>الإجمالى</td>
              {AGING_COLUMNS.map(([key]) => (
                <td key={key} className="num-cell total">
                  {formatMoney(aging.totals[key])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className={isZero(aging.unreconciled) ? 'sub' : 'error'}>
        رصيد الحساب الرقابى {aging.controlAccountCode} فى الدفتر {formatMoney(aging.controlBalance)}{' '}
        • مجموع الأعمار {formatMoney(aging.totals.total)} • غير مطابق{' '}
        {formatMoney(aging.unreconciled)}
        {isZero(aging.unreconciled)
          ? ' — مطابق.'
          : ' — قيود على الحساب الرقابى لا تخص فاتورة أو تسوية (يدوية أو افتتاحية)؛ راجعها.'}
      </p>
    </>
  );
}
